import { useState, useCallback, useMemo } from 'react'
import { useAuth } from './useAuth'
import { useWebSocket } from './useWebSocket'
import { logger } from '@magnetar/utils'

export interface InvestorDeviceStats {
  totalConnectedDevices: number
  totalDisconnectedDevices: number
  connectedClinicsCount: number
  totalClinicsCount: number
  clinicIds: number[]
  clinicStats?: Array<{
    clinicId: number
    connectedDevices: number
    totalDevices: number
    hasOnlineDevices: boolean
    clinicName: string
    broadcastDevices: Array<{
      broadcast_device_id: number
      name: string
      is_active: boolean
      broadcast_type: string
    }>
    onlineDeviceIds: number[]
  }>
  timestamp: string
}

export interface UseInvestorDeviceStatsReturn {
  stats: InvestorDeviceStats | null
  isConnected: boolean
  isConnecting: boolean
  error: string | null
  reconnect: () => void
}

type InvestorClinicStat = NonNullable<InvestorDeviceStats['clinicStats']>[number]

interface InvestorDeviceStatsMessage {
  type?: string
  success?: boolean
  data: InvestorDeviceStats & { message?: string }
}

export function useInvestorDeviceStats(): UseInvestorDeviceStatsReturn {
  const [stats, setStats] = useState<InvestorDeviceStats | null>(null)
  const [error, setError] = useState<string | null>(null)
  const { token } = useAuth()

  const wsProtocol = typeof window !== 'undefined' && window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  const wsHost = import.meta.env.VITE_BROADCAST_SERVICE_URL || 'localhost:9010'
  const wsUrl = token
    ? `${wsProtocol}//${wsHost}/admin/ws/investor-device-stats?token=${encodeURIComponent(token)}`
    : ''

  const connectionKey = useMemo(() => `investor-device-stats-${token?.slice(0, 10) || 'no-token'}`, [token])

  const handleMessage = useCallback((raw: unknown) => {
    try {
      const message = raw as InvestorDeviceStatsMessage
      if (message.type === 'investor_device_stats') {
        const rawData = message.data

        // 在前端重新計算連線和未連線裝置總數
        let totalConnectedDevices = 0
        let totalDisconnectedDevices = 0
        let connectedClinicsCount = 0

        if (rawData.clinicStats && rawData.clinicStats.length > 0) {
          rawData.clinicStats.forEach((clinic: InvestorClinicStat) => {
            totalConnectedDevices += clinic.connectedDevices || 0
            totalDisconnectedDevices += clinic.totalDevices - clinic.connectedDevices || 0

            if (clinic.hasOnlineDevices) {
              connectedClinicsCount++
            }
          })
        }

        // 重新組裝數據
        const processedStats: InvestorDeviceStats = {
          ...rawData,
          totalConnectedDevices,
          totalDisconnectedDevices,
          connectedClinicsCount,
        }

        setStats(processedStats)
        setError(null)
        logger.log('收到投資人設備狀態更新:', processedStats)
      } else if (message.type === 'error') {
        logger.error('WebSocket 錯誤:', message.data)
        setError(message.data.message || '連接錯誤')
      } else if (message.success === true) {
        logger.log('WebSocket 連接成功:', message.data?.message)
      }
    } catch (err) {
      logger.error('處理 WebSocket 訊息失敗:', err)
      setError('數據處理失敗')
    }
  }, [])

  const { isConnected, isConnecting, reconnect, status } = useWebSocket({
    connectionKey,
    url: wsUrl,
    enabled: !!token,
    onMessage: handleMessage,
  })

  return {
    stats,
    isConnected,
    isConnecting,
    error: status === 'error' ? error || '連接失敗' : error,
    reconnect,
  }
}
